import React, { useEffect, useState } from "react";
import { Alert, Button, Snackbar, Stack, Typography } from "@mui/material";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { toast } from "react-toastify";
import { enablePushNotifications, pushSupport } from "../../utils/pushNotifications";

const DISMISS_KEY = "pushPromptDismissedAt";
const REPROMPT_DAYS = 7;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent || "");
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

/**
 * Proactive "turn on notifications" nudge (Slack/Mattermost style). The push
 * pipe (service worker + VAPID + backend) is already wired — this just gets
 * users to grant permission + subscribe so chat messages reach their phone
 * even when the app is closed. Shown once, re-offered after REPROMPT_DAYS.
 *
 * iPhone/iPad can only receive Web Push once the site is installed to the Home
 * Screen (iOS 16.4+), so for non-installed iOS we show install guidance instead
 * of an Enable button that couldn't succeed yet.
 */
export default function PushPrompt() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const iosNeedsInstall = isIOS() && !isStandalone();

  useEffect(() => {
    const support = pushSupport(); // "unsupported" | "default" | "granted" | "denied"
    if (support !== "default") return; // already granted (subscribed silently) / denied / unsupported
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const stale = Date.now() - dismissedAt > REPROMPT_DAYS * 86400000;
    if (dismissedAt && !stale) return;
    const t = setTimeout(() => setOpen(true), 3000); // let the app settle before nudging
    return () => clearTimeout(t);
  }, []);

  const remember = () => localStorage.setItem(DISMISS_KEY, String(Date.now()));
  const dismiss = () => { remember(); setOpen(false); };

  const enable = async () => {
    setBusy(true);
    const result = await enablePushNotifications();
    setBusy(false);
    remember();
    setOpen(false);
    if (result === "enabled") {
      toast.success("🔔 Notifications on — you'll get chat alerts on this device, even when the app is closed");
    } else if (result === "denied") {
      toast.warn("Notifications are blocked for this site — allow them in your browser settings to get chat alerts");
    } else if (result === "unsupported") {
      toast.info("This browser doesn't support push notifications");
    } else {
      toast.error(result.replace(/^error:/, "Couldn't enable notifications: "));
    }
  };

  if (!open) return null;

  return (
    <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "center" }} sx={{ bottom: { xs: 80, md: 24 } }}>
      <Alert
        variant="filled"
        icon={<NotificationsActiveOutlinedIcon />}
        sx={{ alignItems: "center", borderRadius: 3, boxShadow: 8, maxWidth: 470, bgcolor: "primary.main", color: "#fff" }}
        action={
          iosNeedsInstall ? (
            <Button color="inherit" size="small" onClick={dismiss}>Got it</Button>
          ) : (
            <Stack direction="row" spacing={0.5}>
              <Button color="inherit" size="small" onClick={dismiss} disabled={busy}>Not now</Button>
              <Button
                color="inherit" size="small" variant="outlined" onClick={enable} disabled={busy}
                sx={{ borderColor: "rgba(255,255,255,0.7)", fontWeight: 700 }}
              >
                {busy ? "…" : "Enable"}
              </Button>
            </Stack>
          )
        }
      >
        {iosNeedsInstall ? (
          <Typography sx={{ fontSize: 13 }}>
            To get chat alerts on your iPhone: tap <b>Share</b> → <b>Add to Home Screen</b>, then open InfyU from the Home Screen and enable notifications.
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 13 }}>
            Turn on notifications to get chat messages on this device — even when the app or tab is closed.
          </Typography>
        )}
      </Alert>
    </Snackbar>
  );
}
